import { ExtendedBooking } from '@wix/ambassador-bookings-reader-v2-extended-booking/types';
import { ServiceType } from '@wix/ambassador-bookings-services-v2-service/types';
import { DateTimeFormatter } from '@wix/bookings-date-time';
import { SlotService } from '../state/types';

export function formatBookingTime(
  slotService: SlotService,
  existingBookingDetails: ExtendedBooking,
  dateRegionalSettingsLocale: string,
) {
  const timezone =
    slotService.service.type === ServiceType.CLASS
      ? slotService.nestedSlot.timezone
      : undefined;

  const classStartTime =
    existingBookingDetails.booking?.bookedEntity?.slot?.startDate;
  const courseStartTime =
    existingBookingDetails.booking?.bookedEntity?.schedule?.firstSessionStart!;

  const timeFormatter = new DateTimeFormatter(
    dateRegionalSettingsLocale,
    timezone,
  );

  const startTime = timeFormatter.formatTime(classStartTime || courseStartTime);
  return startTime;
}

export function areDatesEqual(date1: string, date2: string) {
  return new Date(date1).getTime() === new Date(date2).getTime();
}

export function hasTimezoneSuffix (date: string) {
  return /(?:[Zz]|[+-]\d{2}:?\d{2})$/.test(date);
} 

export function convertToTimeZone(date: string, timeZone: string): Date {  
  const locallyParsedDate = new Date(date);

  const year = locallyParsedDate.getFullYear();
  const month = locallyParsedDate.getMonth();
  const day = locallyParsedDate.getDate();
  const hour = locallyParsedDate.getHours();
  const minute = locallyParsedDate.getMinutes();
  const second = locallyParsedDate.getSeconds();

  const utcDate = new Date(Date.UTC(year, month, day, hour, minute, second));
  const timezoneOffset = getTimezoneOffset(utcDate, timeZone);
  return new Date(utcDate.getTime() - timezoneOffset);
}

function getTimezoneOffset(utcDate: Date, timeZone: string) {
  let parts;
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23', // Use 24-hour cycle for unambiguous parsing
    });
    parts = formatter.formatToParts(utcDate).reduce((acc: any, part) => {
      if (part.type !== 'literal') {
        acc[part.type] = parseInt(part.value, 10);
      }
      return acc;
    }, {});
  } catch (e) {
    if (e instanceof RangeError) {
      // If the timezone is invalid, return 0 offset (as if it's local time)
      return 0;
    }
    throw e; // re-throw other errors
  }

  const targetTimeAsUtc = new Date(
    Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
    ),
  );

  const timezoneOffset = targetTimeAsUtc.getTime() - utcDate.getTime();
  return timezoneOffset;
}
